import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Box, Typography, CircularProgress, Paper, Button, Divider, Alert } from "@mui/material";
import { CheckCircle } from "@mui/icons-material";
import { useNotification } from "../../contexts/NotificationContext";
import { getSubscriptionByPaymentIntent } from "../../services/subscription.service";

const PaymentSuccess = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { showNotification } = useNotification();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [subscriptionId, setSubscriptionId] = useState(null);
  const [customerId, setCustomerId] = useState(null);

  useEffect(() => {
    const handlePaymentSuccess = async () => {
      try {
        // Extract payment_intent from the URL if it exists
        const params = new URLSearchParams(location.search);
        const paymentIntentId = params.get("payment_intent");
        
        if (!paymentIntentId) {
          setError("Payment information is missing");
          setLoading(false);
          return;
        }
        
        // Get subscription details from payment intent (you may need to create this API endpoint)
        const result = await getSubscriptionByPaymentIntent(paymentIntentId);
        
        if (result && result.data) {
          setSubscriptionId(result.data._id);
          setCustomerId(result.data.customerId);
          showNotification("Payment successful! Your subscription is now active.", "success");
        } else {
          setError("Could not find subscription details");
        }
      } catch (err) {
        console.error("Error processing payment success:", err);
        setError("There was an error processing your payment confirmation");
      } finally {
        setLoading(false);
      }
    };

    handlePaymentSuccess();
  }, [location, showNotification]);

  const handleViewSubscription = () => {
    if (customerId) {
      navigate(`/customers/${customerId}`);
    } else {
      navigate("/subscriptions");
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "60vh" }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 600, mx: "auto", mt: 4 }}>
      <Paper elevation={3} sx={{ p: 3, textAlign: "center" }}>
        {error ? (
          <>
            <Alert severity="warning" sx={{ mb: 3 }}>
              {error}
            </Alert>
            <Typography variant="body1" paragraph>
              Your payment may have been successful, but we couldn't confirm the details.
            </Typography>
            <Button 
              variant="contained" 
              onClick={() => navigate("/subscriptions")}
              sx={{ mt: 2 }}
            >
              View Subscriptions
            </Button>
          </>
        ) : (
          <>
            <CheckCircle color="success" sx={{ fontSize: 64, mb: 2 }} />
            <Typography variant="h4" component="h1" gutterBottom>
              Payment Successful!
            </Typography>
            <Typography variant="body1" paragraph>
              Thank you for your payment. Your subscription has been activated.
            </Typography>
            
            <Divider sx={{ my: 3 }} />
            
            <Box sx={{ mt: 3 }}>
              <Button 
                variant="contained" 
                onClick={handleViewSubscription}
                sx={{ mx: 1 }}
              >
                View Subscription
              </Button>
              <Button 
                variant="outlined" 
                onClick={() => navigate("/")}
                sx={{ mx: 1 }}
              >
                Go to Dashboard
              </Button>
            </Box>
          </>
        )}
      </Paper>
    </Box>
  );
};

export default PaymentSuccess; 